import type { MasterData } from "@h2lca/core";

export const MASTER_COLLECTIONS = [
  { key: "factors", label: "배출계수", itemKey: "id", nameKey: "name" },
  { key: "sources", label: "전력원", itemKey: "id", nameKey: "label" },
  { key: "technologies", label: "대상기술", itemKey: "id", nameKey: "name" },
  { key: "templates", label: "설비 템플릿", itemKey: "id", nameKey: "name" },
  { key: "methods", label: "산정방식", itemKey: "id", nameKey: "label" },
  { key: "examples", label: "교육용 예시", itemKey: "id", nameKey: "name" },
  { key: "glossary", label: "용어", itemKey: "key", nameKey: "term" },
] as const satisfies ReadonlyArray<{ key: keyof MasterData; label: string; itemKey: string; nameKey: string }>;

export type MasterCollectionKey = (typeof MASTER_COLLECTIONS)[number]["key"];
export type MasterChangeKind = "added" | "removed" | "changed";

export interface MasterSnapshotLike {
  data?: Partial<MasterData>;
  origin?: Record<string, "db" | "default">;
}

export interface MasterItemDiff {
  collection: MasterCollectionKey;
  collectionLabel: string;
  key: string;
  label: string;
  change: MasterChangeKind;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changedFields: string[];
}

export interface MasterCollectionDiff {
  key: MasterCollectionKey;
  label: string;
  items: MasterItemDiff[];
  beforeOrigin: "db" | "default" | null;
  afterOrigin: "db" | "default" | null;
}

export interface MasterOriginDiff {
  key: MasterCollectionKey;
  label: string;
  before: "db" | "default" | null;
  after: "db" | "default" | null;
}

export interface MasterDiff {
  collections: MasterCollectionDiff[];
  originChanges: MasterOriginDiff[];
  changedItemCount: number;
  changedFactorCount: number;
  changedReferenceCount: number;
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function snapshotOf(value: unknown): MasterSnapshotLike {
  const snapshot = recordOf(value);
  return {
    data: recordOf(snapshot?.data) as Partial<MasterData> | undefined,
    origin: recordOf(snapshot?.origin) as Record<string, "db" | "default"> | undefined,
  };
}

function stableValue(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableValue).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => `${JSON.stringify(key)}:${stableValue(item)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

function changedFields(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((key) => stableValue(before[key]) !== stableValue(after[key]))
    .sort((a, b) => a.localeCompare(b));
}

function collectionRows(value: unknown, itemKey: string): Map<string, Record<string, unknown>> {
  const rows = new Map<string, Record<string, unknown>>();
  if (!Array.isArray(value)) return rows;
  for (const item of value) {
    const row = recordOf(item);
    const key = row?.[itemKey];
    if (row && typeof key === "string" && key.length > 0) rows.set(key, row);
  }
  return rows;
}

export function compareMasterSnapshots(leftValue: unknown, rightValue: unknown): MasterDiff {
  const left = snapshotOf(leftValue);
  const right = snapshotOf(rightValue);
  const collections = MASTER_COLLECTIONS.map((definition) => {
    const beforeRows = collectionRows(left.data?.[definition.key], definition.itemKey);
    const afterRows = collectionRows(right.data?.[definition.key], definition.itemKey);
    const keys = new Set([...beforeRows.keys(), ...afterRows.keys()]);
    const items = [...keys].map((key): MasterItemDiff | null => {
      const before = beforeRows.get(key) ?? null;
      const after = afterRows.get(key) ?? null;
      const change: MasterChangeKind | null = !before ? "added" : !after ? "removed" : stableValue(before) !== stableValue(after) ? "changed" : null;
      if (!change) return null;
      const record = after ?? before;
      const label = String(record?.[definition.nameKey] ?? key);
      return {
        collection: definition.key,
        collectionLabel: definition.label,
        key,
        label,
        change,
        before,
        after,
        changedFields: change === "changed" && before && after ? changedFields(before, after) : Object.keys(record ?? {}).sort((a, b) => a.localeCompare(b)),
      };
    }).filter((item): item is MasterItemDiff => Boolean(item));
    return {
      key: definition.key,
      label: definition.label,
      items,
      beforeOrigin: left.origin?.[definition.key] ?? null,
      afterOrigin: right.origin?.[definition.key] ?? null,
    };
  });
  const originChanges = collections
    .filter((collection) => collection.beforeOrigin !== collection.afterOrigin)
    .map((collection) => ({ key: collection.key, label: collection.label, before: collection.beforeOrigin, after: collection.afterOrigin }));
  const changedItemCount = collections.reduce((total, collection) => total + collection.items.length, 0);
  const factorCollection = collections.find((collection) => collection.key === "factors");
  return {
    collections,
    originChanges,
    changedItemCount,
    changedFactorCount: factorCollection?.items.length ?? 0,
    changedReferenceCount: changedItemCount - (factorCollection?.items.length ?? 0),
  };
}

export const MASTER_FIELD_LABELS: Record<string, string> = {
  id: "키",
  key: "키",
  name: "이름",
  label: "이름",
  term: "용어",
  kind: "활동 종류",
  value: "값",
  unit: "단위",
  source: "출처",
  sourceUrl: "출처 링크",
  baseYear: "기준연도",
  scope: "적용 범위",
  gwpBasis: "GWP 기준",
  status: "자료 상태",
  isDefault: "기본 계수 여부",
  note: "메모",
  active: "사용 여부",
  efId: "배출계수",
  description: "설명",
  certRenewable: "재생에너지 인정",
  stage: "단계",
  techId: "대상기술",
  icon: "아이콘",
  location: "위치",
  defaultInBoundary: "기본 포함 여부",
  inputs: "입력 항목",
  h2: "수소 흐름 설정",
  order: "표시 순서",
  standards: "적용 기준",
  gwp: "GWP 계수",
  functionalUnit: "기능단위",
  boundary: "시스템 경계",
  denominator: "분모",
  grades: "등급 기준",
  thresholdKgCO2eq: "임계값",
  notices: "안내 문구",
  tags: "태그",
  scenario: "시나리오",
  reference: "기준 결과",
  related: "관련 용어",
  definition: "정의",
};

export function masterFieldLabel(field: string): string {
  return MASTER_FIELD_LABELS[field] ?? field;
}
